import React, { type ComponentProps, type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import Animated, { useAnimatedRef, useScrollOffset } from 'react-native-reanimated';
import { type SafeAreaViewProps, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { CustomNavigationHeader, useNavbarContainerHeight, useResponsive } from '@sg/garnish';

import {
  HorizontalSafeAreaContainer,
  ModalNavbarBackButton,
  TransparentModal,
} from '../../../../components';
import { useNavigateBack } from '../../../../navigation';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardConfirmationModal = (props: PropsWithChildren) => {
  const { children } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();
  const { top: safeAreaTop } = useSafeAreaInsets();
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const navigateBack = useNavigateBack();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetSV = useScrollOffset(scrollViewRef);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const modalDynamicStyles = useStyle(
    () => (currentBreakpoint.isXS ? { paddingTop: safeAreaTop } : undefined),
    [currentBreakpoint.isXS, safeAreaTop],
  );

  const contentContainerStyles = [
    styles.contentContainer,
    currentBreakpoint.isXS ? navbarPaddingBottomStyle : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <HorizontalSafeAreaContainer>
      <TransparentModal
        accessibilityLabel={formatMessage(messages.accessibilityLabel)}
        size={match(['small', 'small', 'medium', 'large'])}
        dialogMaxWidth={currentBreakpoint.isLG ? DIALOG_MAX_WIDTH_LG : undefined}
        dialogMaxHeight={DIALOG_MAX_HEIGHT}
        mobileEdges={MOBILE_SAFE_AREA_EDGES}
        style={modalDynamicStyles}
        onClose={navigateBack}
      >
        {currentBreakpoint.isXS ? null : (
          <CustomNavigationHeader.Container
            transition="gradient"
            safeAreaEdges={HEADER_SAFE_AREA_EDGES}
            scrollOffsetSV={scrollOffsetSV}
          >
            <CustomNavigationHeader.Button.Right
              name="IconClose"
              accessibilityLabel={formatMessage(messages.closeButtonAccessibilityLabel)}
              onPress={navigateBack}
            />
          </CustomNavigationHeader.Container>
        )}

        <Animated.ScrollView ref={scrollViewRef} contentContainerStyle={contentContainerStyles}>
          {children}
        </Animated.ScrollView>
      </TransparentModal>

      <ModalNavbarBackButton />
    </HorizontalSafeAreaContainer>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DIALOG_MAX_HEIGHT = 800;
const DIALOG_MAX_WIDTH_LG = 650;

const MOBILE_SAFE_AREA_EDGES: SafeAreaViewProps['edges'] = [];
const HEADER_SAFE_AREA_EDGES: HeaderSafeAreaEdges = [];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Gift card order confirmation',
    description: 'Gift Card Confirmation Screen | Modal | Accessibility label',
  },
  closeButtonAccessibilityLabel: {
    defaultMessage: 'Close',
    description: 'Gift Card Confirmation Screen | Modal | Close button accessibility label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {
    flexGrow: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HeaderSafeAreaEdges = ComponentProps<typeof CustomNavigationHeader.Container>['safeAreaEdges'];
